import { formatDateTimeToLocal, getMomentValue } from '@devwareapps/devware-cap';
import { CheckRideRequestEntity, CheckrideRequestHomePageQueryEntity, CheckRideRequestPaymentPartyAllItems, CheckRideRequestSubStatusAllItems } from '../../../meta-data/app-meta-data.service';
import * as moment_ from 'moment';

export interface CheckrideCard {
  entity: CheckrideRequestHomePageQueryEntity;
  id: number;
  scheduledMoment: moment_.Moment;
  time: string;
  day: string;
  duration: string;
  applicantName: string;
  examinerName: string;
  flightInstructorName: string;
  gradeClass: string;
  testType: string;
  airport: string;
  flightSchool: string;
  summary: string;
  upcomingSummary: string;
  status: string;
  paymentNotMade: boolean;
  paymentBadge: string;
  categoryClass: string;
  aircraft: string;
  iacraFtn: string;
  scheduledCost: number;
  bookingId: string;
  hasPlanOfAction: boolean;
  canGeneratePlanOfAction: boolean;
}

export function buildCheckrideCard(checkride: CheckrideRequestHomePageQueryEntity,
  paymentViewer: 'examiner' | 'school' | 'applicant' = 'examiner', isOdaSchool = false): CheckrideCard {
  const scheduledMoment = getCheckrideMoment(checkride);
  const duration = formatDuration(checkride.ScheduledTimeMinutes);
  const applicantName = checkride.RoCheckrideApplicantInfoApplicantFinalName;
  const flightSchool = checkride.RoCheckrideApplicantInfoFlightSchoolFinalName;
  const airport = [checkride.AirportAirportCode, checkride.AirportAirportName].filter(Boolean).join(' - ')
    || checkride.ScheduledAirportIdDisplay;
  const paymentNotMade = (checkride.PilexosBookingCost || 0) > 0 && checkride.PilexosBookingPaymentAdded === false;
  const party = checkride.PilexosBookingCostPaymentPartyId;
  const viewerParty = paymentViewer === 'school' ? CheckRideRequestPaymentPartyAllItems.FlightSchool
    : paymentViewer === 'applicant' ? CheckRideRequestPaymentPartyAllItems.Applicant : CheckRideRequestPaymentPartyAllItems.PilotExaminer;
  const partyName = party === CheckRideRequestPaymentPartyAllItems.FlightSchool ? 'school'
    : party === CheckRideRequestPaymentPartyAllItems.Applicant ? 'applicant'
    : party === CheckRideRequestPaymentPartyAllItems.PilotExaminer ? (isOdaSchool ? 'unit member' : 'examiner') : '';

  return {
    entity: checkride,
    id: checkride.CheckRideRequestId,
    scheduledMoment,
    time: scheduledMoment?.format('hh:mm A') || 'None',
    day: scheduledMoment?.format('ddd, MMM D') || 'None',
    duration,
    applicantName: summaryValue(applicantName),
    examinerName: summaryValue(checkride.RoCheckrideApplicantInfoPilotExaminerFinalName),
    flightInstructorName: summaryValue(checkride.RoCheckrideApplicantInfoFlightInstructorFinalName),
    gradeClass: checkride.CheckRideTypeIdDisplay || 'None',
    testType: checkride.CheckRideTestTypeIdDisplay || 'None',
    airport: airport || 'None',
    flightSchool: summaryValue(flightSchool),
    summary: [checkride.CheckRideTypeIdDisplay, checkride.CheckRideTestTypeIdDisplay, airport]
      .map(value => summaryValue(value)).filter(value => !!value).join(' · '),
    upcomingSummary: [checkride.CheckRideTypeIdDisplay, airport]
      .map(value => summaryValue(value)).filter(value => !!value).join(' · '),
    status: summaryValue(checkride.CheckRideRequestSubStatusIdDisplay),
    paymentNotMade,
    paymentBadge: !paymentNotMade ? '' : party === viewerParty ? 'Payment needed'
      : partyName ? `Awaiting ${partyName} payment` : 'Payment outstanding',
    categoryClass: checkride.CheckRideGroupIdDisplay || 'None',
    aircraft: [checkride.AirplaneMakeModel, checkride.AirplaneNNumber].filter(Boolean).join(' — ') || 'None',
    iacraFtn: checkride.AuthorityApplicationNumber || 'None',
    scheduledCost: checkride.ScheduledCheckRideCost,
    bookingId: checkride.CheckRideRequestId?.toString() || 'None',
    hasPlanOfAction: !!checkride.PlanOfAction?.PlanOfActionId,
    canGeneratePlanOfAction: !checkride.PlanOfAction?.PlanOfActionId && !!checkride.ApplicantAcsWrittenResult?.AcsTestId
  };
}

function summaryValue(value: string): string {
  const text = (value || '').trim();
  return text.toLowerCase() === 'none' ? '' : text;
}

function formatDuration(totalMinutes: number): string {
  if (!totalMinutes) {
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];

  if (hours) {
    parts.push(`${hours} hr${hours === 1 ? '' : 's'}`);
  }
  if (minutes) {
    parts.push(`${minutes} min`);
  }

  return parts.join(' ');
}

export function isHomeScheduleCheckride(checkride: CheckRideRequestEntity, today: moment_.Moment): boolean {
  const inactiveStatuses: number[] = [
    CheckRideRequestSubStatusAllItems.Cancelled, CheckRideRequestSubStatusAllItems.Completed,
    CheckRideRequestSubStatusAllItems.Rejected, CheckRideRequestSubStatusAllItems.Deleted,
    CheckRideRequestSubStatusAllItems.Draft, CheckRideRequestSubStatusAllItems.PendingPayment
  ];
  const scheduled = getCheckrideMoment(checkride);
  return !inactiveStatuses.includes(checkride.CheckRideRequestSubStatusId)
    && !!scheduled && scheduled.isValid() && scheduled.isSameOrAfter(today, 'day')
    && scheduled.isSameOrBefore(today.clone().add(7, 'days'), 'day');
}

function getCheckrideMoment(checkride: CheckRideRequestEntity): moment_.Moment {
  if (!checkride?.ScheduledDateTime) {
    return null;
  }

  const localDate = formatDateTimeToLocal(checkride.ScheduledDateTime, true, true);
  return getMomentValue(localDate, true, true);
}

